/**
 * Utility function to get CSS custom property value from document root
 */
function getCssVariable(variableName: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
}

/**
 * Centralized Tailwind color definitions for SPOG Client
 * All colors are resolved from CSS custom properties defined in tailwind.config.js
 */
export const TailwindColors = {
  // Primary colors
  primarySurface: getCssVariable('--color-primary-surface'),
  primaryBlack: getCssVariable('--color-primary-black'),
  primaryWhite: getCssVariable('--color-primary-white'),

  // Primary Gray colors
  gray50: getCssVariable('--color-primary-gray-50'),
  gray100: getCssVariable('--color-primary-gray-100'),
  gray200: getCssVariable('--color-primary-gray-200'),
  gray300: getCssVariable('--color-primary-gray-300'),
  gray400: getCssVariable('--color-primary-gray-400'),
  gray500: getCssVariable('--color-primary-gray-500'),
  grayMenuSelected: getCssVariable('--color-primary-gray-menu-selected'),
  grayHeaderLight: getCssVariable('--color-primary-gray-header-light'),
  grayHeader: getCssVariable('--color-primary-gray-header'),
  grayHeaderBg: getCssVariable('--color-primary-gray-header-bg'),
  grayRowAlt: getCssVariable('--color-primary-gray-row-alt'),
  grayLight: getCssVariable('--color-primary-gray-light'),

  // Tertiary Blue colors
  tertiaryBlueDeep: getCssVariable('--color-tertiary-blue-deep'),
  tertiaryBlueCta: getCssVariable('--color-tertiary-blue-cta'),
  tertiaryBlueLink: getCssVariable('--color-tertiary-blue-link'),
  tertiaryBlueLight: getCssVariable('--color-tertiary-blue-light'),
  tertiaryBlueEditCell: getCssVariable('--color-tertiary-blue-edit-cell'),
  tertiaryBlueActionButtonRefreshIcon: getCssVariable(
    '--color-tertiary-blue-action-button-refresh-icon'
  ),
  tertiaryBlueActionButtonBg: getCssVariable(
    '--color-tertiary-blue-action-button-bg'
  ),
  tertiaryBlueToggleTrackBg: getCssVariable(
    '--color-tertiary-blue-toggle-track-bg'
  ),

  // Tertiary Orange colors
  tertiaryOrangeDark: getCssVariable('--color-tertiary-orange-dark'),

  // Tertiary Yellow colors
  tertiaryYellowLight: getCssVariable('--color-tertiary-yellow-light'),

  // Tertiary Green
  tertiaryGreen: getCssVariable('--color-tertiary-green-light'),
  tertiaryGreenDark: getCssVariable('--color-tertiary-green-dark'),

  // CDW Brand colors
  cdwRedPrimary: getCssVariable('--color-cdw-red-primary'),
  cdwRedCorpDark: getCssVariable('--color-cdw-red-corp-dark'),

  // Chart colors (contrast/saturated fills for gauges and donut charts)
  chartYellow: getCssVariable('--color-chart-yellow'),
  chartBlue: getCssVariable('--color-chart-blue'),
  chartGreen: getCssVariable('--color-chart-green'),
  chartRed: getCssVariable('--color-chart-red'),
  chartDarkOrange: getCssVariable('--color-tertiary-orange-dark'),

  // Gopher branding
  gopherBlue: getCssVariable('--color-gopher-blue'),

  // Pastel colors (row/header backgrounds for reports)
  pastelRose: getCssVariable('--color-pastel-rose'),
  pastelPeach: getCssVariable('--color-pastel-peach'),
  pastelLemon: getCssVariable('--color-pastel-lemon'),
  pastelLime: getCssVariable('--color-pastel-lime'),
  pastelPeriwinkle: getCssVariable('--color-pastel-periwinkle'),
  pastelSage: getCssVariable('--color-pastel-sage'),
  pastelCoral: getCssVariable('--color-pastel-coral'),

  // Legacy/Alias (for backward compatibility)
  dialogHeader: getCssVariable('--dialog-header'),
  actionButtonBg: getCssVariable('--color-action-button-bg'),
} as const;

/**
 * Type-safe keys for TailwindColors
 */
export type TailwindColorKey = keyof typeof TailwindColors;
